import * as _ from 'lodash';
import {
  findFieldsNode,
  findValueFromFields,
  getMetaObjectID,
  logger,
  parseProductImages,
  slugify,
} from '~/utils';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import {ProductGallery} from '../ProductGallery';
import {useFetcher} from '@remix-run/react';
import {useInView} from 'react-intersection-observer';
import {useEffect, useMemo, useRef} from 'react';

const PropertiesDataSheetCard = ({
  properties,
  id,
  title,
}: {
  properties: any;
  id: number;
  title: string;
}) => {
  return (
    <div className="mt-10">
      <AccordionItem uuid={id}>
        <AccordionItemHeading>
          <AccordionItemButton>
            <span>{title}</span>
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <div className="row">
            <table>
              <thead>
                <tr className="products-details-tabs">
                  <th>Property</th>
                  <th>Definition</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {_.map(properties, (item, index) => (
                  <tr key={`property-${index}`}>
                    <td>{_.get(item, 'name')}</td>
                    <td>{_.get(item, 'propertyBase.name')}</td>
                    <td>{_.get(item, 'propertyValue')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AccordionItemPanel>
      </AccordionItem>
    </div>
  );
};

const DataSheetItemsPropCard = ({
  properties,
  id,
  title,
  description,
  images,
}: {
  properties: any;
  id: number;
  title: string;
  description: string;
  images?: string[];
}) => {
  return (
    <div className="mt-10">
      <AccordionItem uuid={id}>
        <AccordionItemHeading>
          <AccordionItemButton>
            <span style={{marginRight: 23}}>{title}</span>
            &nbsp;
            <span>{description}</span>
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <div className="row">
            <ProductGallery
              showMainGallery={false}
              media={parseProductImages(images)}
            />
            <table>
              <thead>
                <tr className="products-details-tabs">
                  <th>Property</th>
                  <th>Definition</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {_.map(properties, (item, index) => (
                  <tr
                    key={`property-bom-prop-${index}-${slugify(
                      _.get(item, 'name'),
                    )}`}
                  >
                    <td>{_.get(item, 'name')}</td>
                    <td>{_.get(item, 'propertyBase.name')}</td>
                    <td>{_.get(item, 'propertyValue')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AccordionItemPanel>
      </AccordionItem>
    </div>
  );
};
const DataSheet = ({
  productProperties,
  productBom,
  metaobjectIds,
  properties,
  items,
}: {
  productProperties: any;
  productBom: any;
  metaobjectIds: any;
  properties: any;
  items: any;
}) => {
  const fetcher = useFetcher();
  const metaobjects = useMemo(() => {
    return _.get(fetcher.data, 'metaobjects');
  }, [fetcher.data]);
  let ids = '';
  _.map(metaobjectIds, (id, idx) => {
    const comma = idx === 0 ? '' : ',';
    ids += `${comma}${getMetaObjectID(id)}`;
  });

  const {ref, inView} = useInView({
    threshold: 0,
    triggerOnce: true,
  });
  const observerRef = useRef(null);
  useEffect(() => {
    ref(observerRef.current);
  }, [ref, observerRef]);

  useEffect(() => {
    if (!inView || fetcher.data || fetcher.state === 'loading') return;
    fetcher.load(`/api/metaobjects/search?ids=${ids}`);
  }, [inView, fetcher]);
  return (
    <>
      <div className="products-description" ref={observerRef}>
        <Accordion preExpanded={[1]} allowZeroExpanded allowMultipleExpanded>
          <PropertiesDataSheetCard
            title="Product Property"
            id={1}
            properties={properties}
          />
        </Accordion>
        <Accordion preExpanded={[1]} allowZeroExpanded allowMultipleExpanded>
          {_.map(items, (bom, index: number) => {
            const qty = _.get(bom, 'qty');
            const name = _.get(bom, 'name');
            const item = _.get(bom, 'item');

            const description = _.get(item, 'name');
            const properties = _.find(
              metaobjects,
              (metaobject) => _.get(metaobject, 'id') === _.get(item, 'id'),
            );
            let main_media = findValueFromFields(properties, 'main_media');
            let additional_media = findValueFromFields(
              properties,
              'additional_media',
            );
            let images = main_media ? [main_media] : [];

            if (additional_media) {
              additional_media = JSON.parse(additional_media);
            }

            _.map(additional_media, (image) => {
              if (image) {
                images.push(image);
              }
            });

            let propUse = findValueFromFields(properties, 'prop_use');
            if (propUse) {
              propUse = _.get(JSON.parse(propUse), 'data');
            }
            return (
              <>
                <DataSheetItemsPropCard
                  key={`${index}-${name}-${new Date().toString()}`}
                  title={`${qty} x ${name}`}
                  description={description}
                  id={index + 1}
                  properties={propUse || []}
                  images={images || []}
                />
              </>
            );
          })}
        </Accordion>
      </div>
    </>
  );
};

export default DataSheet;
